import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Settings, LogOut, Menu, X, Tag, ShoppingBag, Radio, Megaphone, Medal } from 'lucide-react';
import { useAuth, logout } from '../../services/firebase';

const AdminLayout: React.FC = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const isActive = (path: string) => location.pathname === path;

    return (
        <div className="flex h-screen bg-gray-100 font-sans text-gray-900">
            {/* Sidebar */}
            <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col shadow-xl z-20`}>
                <div className="p-4 flex items-center justify-between border-b border-slate-800 h-16 shrink-0">
                    {sidebarOpen && <h1 className="font-bold text-xl tracking-wider text-amber-500">ACME<span className="text-white text-xs block text-opacity-50">ADMIN PANEL</span></h1>}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-slate-800 rounded">
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="flex-1 py-6 space-y-2 px-2">
                    <Link to="/admin" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <LayoutDashboard size={20} />
                        {sidebarOpen && <span>Dashboard</span>}
                    </Link>
                    <Link to="/admin/cases" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/cases') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <FileText size={20} />
                        {sidebarOpen && <span>Casos</span>}
                    </Link>
                    <Link to="/admin/users" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/users') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Users size={20} />
                        {sidebarOpen && <span>Usuários</span>}
                    </Link>
                    <Link to="/admin/ranks" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/ranks') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Medal size={20} />
                        {sidebarOpen && <span>Patentes</span>}
                    </Link>
                    <Link to="/admin/codes" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/codes') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Tag size={20} />
                        {sidebarOpen && <span>Códigos VIP</span>}
                    </Link>
                    <Link to="/admin/store" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/store') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <ShoppingBag size={20} />
                        {sidebarOpen && <span>Loja & Financeiro</span>}
                    </Link>
                    <Link to="/admin/announcements" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/announcements') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Megaphone size={20} />
                        {sidebarOpen && <span>Quadro de Avisos</span>}
                    </Link>
                    <Link to="/admin/push" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/push') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Radio size={20} />
                        {sidebarOpen && <span>Alertas Push</span>}
                    </Link>
                    <Link to="/admin/settings" className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${isActive('/admin/settings') ? 'bg-amber-600 text-white' : 'hover:bg-slate-800 text-gray-400'}`}>
                        <Settings size={20} />
                        {sidebarOpen && <span>Configurações</span>}
                    </Link>
                </nav>

                <div className="p-4 border-t border-slate-800">
                    <button onClick={handleLogout} className="flex items-center gap-4 px-4 py-3 w-full rounded-lg hover:bg-red-900/50 text-red-400 transition-colors">
                        <LogOut size={20} />
                        {sidebarOpen && <span>Sair</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 shadow-sm">
                    <h2 className="text-xl font-bold text-gray-700 capitalize">{location.pathname.split('/').pop() || 'Dashboard'}</h2>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500">Olá, {user?.displayName || 'Admin'}</span>
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
                            {user?.displayName?.[0] || 'A'}
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-auto p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
